"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { BookOpen, HeartHandshake, Shirt, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const processSteps = [{
  id: "01",
  title: "Educate & Empathize",
  description: "We make important issues easier to understand by sharing the problem, the people affected, and the reason each cause deserves continued attention.",
  icon: BookOpen,
  idClassName: "text-card-foreground flex flex-col gap-6 py-6 group !bg-[url(https://prime-egret.10web.cloud/wp-content/uploads/2026/07/Website-pic-8.png)] group relative bg-card border border-border/30 shadow-sm hover:-translate-y-1 hover:shadow-md hover:border-primary/30 transition-all duration-300 rounded-2xl overflow-hidden !bg-[url(https://prime-egret.10web.cloud/wp-content/uploads/2026/07/Website-pic-7.png)]"
}, {
  id: "02",
  title: "Wear What Matters",
  description: "We release limited-edition merch connected to causes that deserve attention. Every piece is designed to carry a message, start a conversation, and help keep awareness alive beyond the moment.",
  icon: Shirt
}, {
  id: "03",
  title: "Fund the Frontlines",
  description: "A portion of every purchase helps support organizations, families, communities, or efforts connected to the cause — turning awareness into real support beyond the message.",
  icon: HeartHandshake
}];
export default function Process() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const imageReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const stepsReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="how-forever-fresh-works" className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute left-4 md:left-8 lg:left-[2.5vw] top-0 bottom-0 w-px bg-border/20 hidden md:block">
        <div className="absolute top-32 left-1/2 -translate-x-1/2 size-1.5 rounded-lg bg-primary" />
      </div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-[5vw]">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 mb-20 md:mb-32">
          {}
          <div ref={headerReveal.ref} className={`lg:col-span-7 flex flex-col justify-center transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {}
            <div className="flex items-center gap-4 mb-8">
              <span className="font-mono text-xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">
                Section 02 // The Process
              </span>
              <div className="h-px flex-1 bg-border/30 max-w-[100px]" />
            </div>

            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] mb-6 text-foreground">A platform for <span className="italic font-serif text-primary" data-ast-id="f:/src/pages/Home/sections/Process.tsx|sig:qnz7t8" data-ast-start="4184" data-ast-end="4246">impact</span>.</h2>

            <p className="font-sans text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl mb-10">
              We believe that true change starts with understanding. Forever Fresh
              bridges the gap between high-impact humanitarian advocacy and
              elevated, purpose-driven lifestyle branding. Here is how we turn
              awareness into action.
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-lg bg-foreground text-background hover:bg-primary hover:text-primary-foreground transition-colors duration-300 px-8 h-12 text-base">
                <Link to="/CurrentCausesPage">
                  Explore Causes
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-lg border-border/50 bg-transparent hover:bg-accent hover:text-accent-foreground transition-colors duration-300 px-8 h-12 text-base">
                <Link to="/ShopPage">Shop Current Drop</Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={imageReveal.ref} className={`lg:col-span-5 relative transition-all duration-1000 delay-200 ease-[cubic-bezier(0.22,1,0.36,1)] ${imageReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {}
            <div className="absolute -top-3 -left-3 text-muted-foreground/50 font-mono text-xs">+</div>
            <div className="absolute -top-3 -right-3 text-muted-foreground/50 font-mono text-xs">+</div>
            <div className="absolute -bottom-3 -left-3 text-muted-foreground/50 font-mono text-xs">+</div>
            <div className="absolute -bottom-3 -right-3 text-muted-foreground/50 font-mono text-xs">+</div>

            <div className="relative aspect-[4/5] w-full rounded-2xl overflow-hidden bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/process_community_action.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/process_community_action.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/process_community_action.png-wvc-sizes" alt="Community members wearing purpose-driven merchandise and engaging in a local cause" className="object-cover w-full h-full filter sepia-[0.1] contrast-[1.05]" loading="lazy" src="https://prime-egret.10web.cloud/wp-content/uploads/2026/06/Website-pic-4.png" src="https://prime-egret.10web.cloud/wp-content/uploads/2026/07/Website-pic-8.png" />
              {}
              <div className="absolute inset-0 bg-foreground/5 mix-blend-overlay" />
            </div>
          </div>
        </div>

        {}
        <div ref={stepsReveal.ref} className={`grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 transition-all duration-1000 delay-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${stepsReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {processSteps.map((step, i) => <Card key={step.id} data-index={i} className="text-card-foreground flex flex-col gap-6 py-6 group group group relative border border-border/30 shadow-sm transition-all duration-300 rounded-2xl overflow-hidden !bg-[rgba(255,255,255,1)] hover:-translate-y-1 hover:shadow-md hover:border-primary/30">
              <CardContent className="p-8 md:p-10 flex flex-col h-full">
                <div className="flex items-start justify-between mb-8">
                  <div className="p-3 rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                    <step.icon className="size-6" strokeWidth={1.5} />
                  </div>
                  
                </div>
                
                <h3 className="font-serif text-xl md:text-2xl font-medium text-foreground mb-4">
                  {step.title}
                </h3>
                
                <p className="font-sans text-base text-muted-foreground leading-relaxed mt-auto">
                  {step.description}
                </p>
              </CardContent>
            </Card>)}
        </div>
      </div>
    </section>;
}